import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Tag, Trash2, RefreshCw, Settings } from 'lucide-react';
import api from '../../lib/api';

export function DataUtilities() {
  const [inactiveMappingCount, setInactiveMappingCount] = useState(0);
  const [purgingMappings, setPurgingMappings] = useState(false);

  useEffect(() => {
    loadInactiveMappingCount();
  }, []);

  const loadInactiveMappingCount = async () => {
    try {
      const response = await api.get('/admin/inactive-mapping-count');
      setInactiveMappingCount(response.data.count || 0);
    } catch {
      // Silently handle
    }
  };

  const handlePurgeInactiveMappings = async () => {
    if (!window.confirm('Permanently delete all deactivated supplier codes?')) return;
    setPurgingMappings(true);
    try {
      const response = await api.delete('/admin/purge-inactive-mappings');
      toast.success(`Purged ${response.data.deleted || 0} inactive mappings`);
      setInactiveMappingCount(0);
    } catch (error) {
      toast.error('Failed to purge: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurgingMappings(false);
    }
  };

  return (
    <Card data-testid="data-utilities">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" /> Data Utilities
        </CardTitle>
        <CardDescription>Maintenance tasks and data synchronization</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 border rounded-lg bg-blue-50 space-y-3">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-800">Backfill Customer Product Codes</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            Updates all existing dispatch lines with supplier product codes from your mappings.
            Use this after adding new supplier code mappings to populate historical dispatches.
          </p>
          <Button
            variant="outline"
            className="border-blue-400 text-blue-700 hover:bg-blue-100"
            onClick={async () => {
              try {
                const response = await api.post('/admin/backfill-supplier-codes');
                const data = response.data;
                if (data.updated_lines > 0) {
                  toast.success(`Updated ${data.updated_lines} lines in ${data.updated_dispatches} dispatches`);
                } else {
                  toast.info('No dispatches needed updating');
                }
              } catch {
                toast.error('Failed to backfill supplier codes');
              }
            }}
            data-testid="btn-backfill-supplier-codes"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Backfill Customer Codes
          </Button>
        </div>

        <div className="p-4 border rounded-lg bg-orange-50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-600" />
              <h4 className="font-medium text-orange-800">Purge Deactivated Customer Codes</h4>
            </div>
            {inactiveMappingCount > 0 && (
              <Badge variant="destructive" className="text-xs">{inactiveMappingCount} inactive</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Permanently removes all deactivated supplier product code mappings from the database.
            Active mappings are not affected. This action cannot be undone.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-orange-400 text-orange-700 hover:bg-orange-100"
              onClick={handlePurgeInactiveMappings}
              disabled={purgingMappings || inactiveMappingCount === 0}
              data-testid="btn-purge-inactive-mappings"
            >
              {purgingMappings ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Purging...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Purge {inactiveMappingCount} Deactivated Codes</>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={loadInactiveMappingCount} data-testid="btn-refresh-inactive-count">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function FeatureToggles() {
  const [featureToggles, setFeatureToggles] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    loadFeatureToggles();
  }, []);

  const loadFeatureToggles = async () => {
    try {
      const response = await api.get('/settings/feature-toggles');
      setFeatureToggles(response.data || {});
    } catch {
      // Silently handle
    }
  };

  const handleToggleFeature = async (key, value) => {
    setSaving(key);
    try {
      const res = await api.put('/settings/feature-toggles', { [key]: value });
      setFeatureToggles(res.data?.toggles || { ...featureToggles, [key]: value });
      window.dispatchEvent(new Event('feature-toggles-changed'));
      toast.success(`Feature ${value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle feature');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card className="border-purple-200 bg-purple-50/30" data-testid="feature-toggles">
      <CardHeader>
        <CardTitle className="text-purple-700 flex items-center gap-2">
          <Settings className="w-5 h-5" /> Feature Toggles
        </CardTitle>
        <CardDescription>Enable or disable application features. Changes take effect immediately.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
            <div>
              <h4 className="font-medium text-sm">Events / Trade Shows Module</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Trade show &amp; event stock management: attach pipeline orders, record returns,
                reconcile Sold / Waste / Samples and post stock quantities to AccountsIQ.
                When enabled, "Events" appears in the sidebar navigation.
              </p>
            </div>
            <Switch
              checked={featureToggles.events_module_enabled || false}
              disabled={saving === 'events_module_enabled'}
              onCheckedChange={(checked) => handleToggleFeature('events_module_enabled', checked)}
              data-testid="toggle-events-module"
            />
          </div>
          <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
            <div>
              <h4 className="font-medium text-sm">Transformations Module</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Product transformation tracking (e.g., cutting cheese wheels into portions).
                When enabled, appears in the sidebar navigation.
              </p>
            </div>
            <Switch
              checked={featureToggles.transformations_enabled || false}
              disabled={saving === 'transformations_enabled'}
              onCheckedChange={(checked) => handleToggleFeature('transformations_enabled', checked)}
              data-testid="toggle-transformations"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
